// src/services/support.js — talking to the official Allo account.
//
// 🟢 THE WHOLE CONVERSATION IS AN ORDINARY CHAT. Everything below just drives
//    the server's state machine; the messages themselves arrive through the same
//    watchMessages the rest of the app uses, and the chat appears in the chat
//    list like any other. There is no second message store and no second UI.
//
// 🟥 AND IT IS THE ONE CHAT THAT IS NOT ENCRYPTED. It cannot be: the other party
//    is the Allo team, and someone has to read a support ticket to answer it.
//    `isOfficialChat()` is what every surface uses to say so out loud, to hide
//    the call buttons, and to drop translate — one flag rather than four
//    conditions that would drift apart.

import { auth } from '../config/firebase';
/* 🖼 the one image normaliser — see services/mediaTools.js */
import { normalizeImageFile } from './mediaTools';

const BACKEND = process.env.REACT_APP_BACKEND_URL
  || 'https://signaling-server-prod-397203671414.us-central1.run.app';

export const OFFICIAL_UID = 'allo_official';

/** Is this the Allo account? Reads the chat doc, with the uid as a fallback. */
export function isOfficialChat(chat) {
  if (!chat) return false;
  if (chat.isOfficial === true) return true;
  return Array.isArray(chat.participants) && chat.participants.includes(OFFICIAL_UID);
}

export const isOfficialUid = (uid) => uid === OFFICIAL_UID;

/** The support state machine's current position: menu | compose | submitted | replied */
export const supportState = (chat) => (isOfficialChat(chat) ? (chat?.support?.state || 'menu') : null);

/** May the person type right now? */
export const supportCanWrite = (chat) => ['compose', 'replied'].includes(supportState(chat));

async function call(path, body = {}) {
  const u = auth.currentUser;
  if (!u) throw new Error('unauthenticated');
  const res = await fetch(`${BACKEND}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${await u.getIdToken()}` },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.ok === false) throw new Error(data.error || 'failed');
  return data;
}

export const openSupport = () => call('/api/support/open');
export const chooseTopic = (category) => call('/api/support/choose', { category });
export const escalate = (category) => call('/api/support/escalate', { category });
export const restartSupport = () => call('/api/support/restart');

export const submitTicket = (body, media = []) => call('/api/support/submit', {
  body,
  media,
  /* 🟢 THE BARE URL LIST GOES TOO. The staff console indexes `attachments`,
     and a queue that stops showing files the day the client gets richer is a
     migration nobody asked for. The server prefers `media` and falls back. */
  attachments: media.map((m) => m.url),
  /* 🟢 THE DEVICE FACTS GO WITHOUT BEING ASKED FOR. "What browser are you on?"
     is the question every support thread wastes its first reply on, and almost
     nobody knows the answer. Collecting it here costs nothing and saves a
     round trip that would otherwise take a day. */
  context: {
    platform: 'web',
    appVersion: process.env.REACT_APP_VERSION || 'web',
    osVersion: navigator.platform || '',
    locale: navigator.language || '',
  },
});

/**
 * What kind of thing is this, in the four words the official account speaks?
 *
 * 🟥 THIS CHAT USED TO UNDERSTAND EXACTLY ONE: a screenshot. Anything else was
 *    written as an image, so a video arrived as a broken picture and a PDF as a
 *    picture of nothing. Twin of app/services/support.ts — same four kinds, in
 *    the same order, because the server checks them against one list.
 */
export function supportFileKind(f) {
  const mime = String(f?.type || f?.mimeType || '').toLowerCase();
  const tail = String(f?.name || '').toLowerCase();
  if (mime.startsWith('video/') || /\.(mp4|mov|m4v|3gp|webm)$/.test(tail)) return 'video';
  if (mime.startsWith('audio/') || /\.(m4a|mp3|aac|wav|ogg)$/.test(tail)) return 'audio';
  if (mime.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp|heic|heif)$/.test(tail)) return 'image';
  return 'document';
}

/**
 * Upload one file for a ticket and return its public URL.
 *
 * 🟥 DELIBERATELY NOT ENCRYPTED, UNLIKE EVERY OTHER FILE THIS APP SENDS. A
 *    file attached to a support ticket exists so a person on the Allo team can
 *    open it; sealing it to a key they do not have would make it useless for its
 *    only purpose. The UI says this in words before the file picker opens — a
 *    user who has been told their photos are end-to-end encrypted is owed an
 *    explicit exception, not a quiet one.
 *
 * 🟥 IT WAS CALLED `uploadScreenshot` AND THE NAME WAS THE DESIGN: an
 *    image-only picker in the one chat where people are already having trouble.
 *    The bytes it moves were never image-specific.
 */
export async function uploadSupportFile(input, onProgress) {
  /* 🖼 🟥 AN IPHONE'S HEIC IS A BROKEN IMAGE EVERYWHERE ELSE — in this browser,
     in the other person's browser, and in the staff console that has to look at
     it to answer the ticket. A screenshot AirDropped to a laptop and attached
     here went up untouched, because this path never went near `mediaTools`.
     `normalizeImageFile` returns anything that is not one exactly as it was. */
  const file = await normalizeImageFile(input);
  const u = auth.currentUser;
  if (!u) throw new Error('unauthenticated');
  const token = await u.getIdToken();

  const pre = await fetch(`${BACKEND}/api/r2/presign-upload`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({}),
  }).then((r) => r.json());
  if (!pre?.uploadUrl) throw new Error('no_upload_url');

  await new Promise((resolve, reject) => {
    /* XHR rather than fetch, only because fetch still cannot report upload
       progress and a file on a slow connection needs a bar */
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', pre.uploadUrl);
    xhr.setRequestHeader('Content-Type', 'application/octet-stream');
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress?.(e.loaded / e.total);
    };
    xhr.onload = () => ((xhr.status >= 200 && xhr.status < 300) ? resolve() : reject(new Error(`http_${xhr.status}`)));
    xhr.onerror = () => reject(new Error('network'));
    xhr.send(file);
  });

  return pre.publicUrl;
}
